import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '@nanostores/react';
import {
  projectAssetsStore,
  removeProjectAsset,
  toggleAssetReference,
  attachAllAssets,
  detachAllAssets,
  isAssetListPopupOpen,
  closeAssetListPopup,
} from '~/lib/stores/assets';
import { formatDistanceToNow } from 'date-fns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { classNames } from '~/utils/classNames';

export const AssetListPopup: React.FC = () => {
  const assets = useStore(projectAssetsStore);
  const isOpen = useStore(isAssetListPopupOpen);

  const canAttachAll = assets.some((asset) => !asset.isReferenced);

  const handleClose = () => {
    closeAssetListPopup();
  };

  const handleDelete = (jobId: string) => {
    removeProjectAsset(jobId);
  };

  const handleToggleReference = async (jobId: string) => {
    await toggleAssetReference(jobId);
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) {
      return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-[500px] bg-[#1A1A1A] border border-[#333] rounded-lg shadow-2xl overflow-hidden flex flex-col h-[450px]"
          >
            <div className="p-4 border-b border-[#333] flex justify-between items-center bg-[#1A1A1A] shrink-0">
              <div className="flex items-center gap-4">
                <h3 className="text-white font-medium text-sm">Project Assets</h3>
                {assets.length > 0 && canAttachAll ? (
                  <button
                    onClick={async () => await attachAllAssets()}
                    className="text-[11px] text-gray-400 hover:text-white transition-colors"
                  >
                    Attach All
                  </button>
                ) : (
                  <button
                    onClick={async () => await detachAllAssets()}
                    className="text-[11px] text-gray-400 hover:text-white transition-colors"
                  >
                    Detach All
                  </button>
                )}
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
                <div className="i-ph:x-bold text-sm" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0">
              {assets.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">No assets found for this project.</div>
              ) : (
                <div className="divide-y divide-[#222]">
                  {assets.map((asset) => (
                    <div
                      key={asset.jobId}
                      className="flex items-center gap-3 p-3 hover:bg-[#222] transition-colors group"
                    >
                      <button
                        onClick={async () => await handleToggleReference(asset.jobId)}
                        className={classNames(
                          'w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0',
                          asset.isReferenced
                            ? 'bg-white/80 border-white text-black'
                            : 'bg-transparent border-[#444] hover:border-gray-500',
                        )}
                      >
                        {asset.isReferenced && <div className="i-ph:check-bold text-[10px]" />}
                      </button>

                      <div className="w-10 h-10 rounded bg-[#2A2A2A] flex items-center justify-center shrink-0 overflow-hidden">
                        {asset.gcsUrl && <img src={asset.gcsUrl} alt="" className="w-full h-full object-cover" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium truncate" title={asset.fileName}>
                          {asset.fileName}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span>{asset.mimeType}</span>
                          <span>•</span>
                          <span>{formatSize(asset.size)}</span>
                          {asset.createdAt && (
                            <>
                              <span>•</span>
                              <span>{formatDistanceToNow(new Date(asset.createdAt), { addSuffix: true })}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button className="p-1 rounded hover:bg-[#333] text-gray-400 hover:text-white transition-colors">
                            <div className="i-ph:dots-three-vertical-bold text-lg" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            className="bg-[#2A2A2A] border border-[#444] rounded-md p-1 shadow-xl z-60 min-w-[100px]"
                            sideOffset={5}
                            align="end"
                          >
                            <DropdownMenu.Item
                              className="flex items-center gap-2 px-2 py-1.5 text-xs text-red-400 hover:bg-red-400/10 hover:text-red-300 rounded cursor-pointer outline-none transition-colors"
                              onClick={() => handleDelete(asset.jobId)}
                            >
                              <div className="i-ph:trash-bold" />
                              Delete
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
